// packages/ui/src/surface/WidgetSurface.ts
import { createContext, useContext } from 'react';

/**
 * Where a widget's elements live (docs/embed-shadow-dom.md). On a partner's page the embed seals itself in a shadow
 * root, so the page's CSS cannot reach it: its own elements are in that root, and its dialogs, menus, tooltips and
 * other floating elements render into its overlay layer there, which the browser shows in its top layer, instead of
 * into the page's body. Where nothing is sealed (a page that is TourTask's own, or a browser that cannot seal the
 * widget), `root` and `overlays` are null and everything renders where MUI renders it by default.
 */
export interface WidgetSurface {
	/** The shadow root the widget renders in; null = the document. */
	readonly root: ShadowRoot | null;
	/** The layer floating elements render into (MUI's portals, tooltips, the add-to-cart flight); null = the document's body. */
	readonly overlays: HTMLElement | null;
	/**
	 * Seals an element the widget places in the page itself (the cart bar beside the page's header): gives it a shadow
	 * root that carries the widget's styles, and returns the element its content renders into. Unsealed: `host` itself.
	 */
	readonly seal: (host: HTMLElement) => HTMLElement;
	/** The widget's own element with this id, in whichever of its trees it is (its shadow roots, or the document). */
	readonly getElementById: (id: string) => HTMLElement | null;
}

/** Nothing sealed: the document, and its body for floating elements. */
export const UNSEALED_SURFACE: WidgetSurface = {
	root: null,
	overlays: null,
	seal: (host) => host,
	getElementById: (id) => document.getElementById(id),
};

export const WidgetSurfaceContext = createContext<WidgetSurface>(UNSEALED_SURFACE);

export function useWidgetSurface(): WidgetSurface {
	return useContext(WidgetSurfaceContext);
}
